/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { jsonWebKeySetsDelete } from "../funcs/jsonWebKeySetsDelete.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  DeleteJsonWebKeySetRequest,
  DeleteJsonWebKeySetSecurity,
} from "../models/operations/deletejsonwebkeyset.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type DeleteJsonWebKeySetMutationVariables = {
  request: DeleteJsonWebKeySetRequest;
  security?: DeleteJsonWebKeySetSecurity | undefined;
  options?: RequestOptions;
};

export type DeleteJsonWebKeySetMutationData = void;

export type DeleteJsonWebKeySetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * deleteSet jsonWebKeySets
 *
 * @remarks
 * Soft-delete a JSON Web Key Set by ID, withdrawing every key still published in it in the same operation. Refused with a conflict while any live remote_session_client still references the set — detach it there first; see getDeletePreflight. Requires org:admin. Tokens signed with the set's keys stop verifying, so treat this as decommissioning the set's whole trust anchor rather than tidying up.
 */
export function useDeleteJsonWebKeySetMutation(
  options?: MutationHookOptions<
    DeleteJsonWebKeySetMutationData,
    DeleteJsonWebKeySetMutationError,
    DeleteJsonWebKeySetMutationVariables
  >,
): UseMutationResult<
  DeleteJsonWebKeySetMutationData,
  DeleteJsonWebKeySetMutationError,
  DeleteJsonWebKeySetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildDeleteJsonWebKeySetMutation(client, options),
    ...options,
  });
}

export function mutationKeyDeleteJsonWebKeySet(): MutationKey {
  return ["@gram/client", "jsonWebKeySets", "delete"];
}

export function buildDeleteJsonWebKeySetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: DeleteJsonWebKeySetMutationVariables,
  ) => Promise<DeleteJsonWebKeySetMutationData>;
} {
  return {
    mutationKey: mutationKeyDeleteJsonWebKeySet(),
    mutationFn: function deleteJsonWebKeySetMutationFn({
      request,
      security,
      options,
    }): Promise<DeleteJsonWebKeySetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(jsonWebKeySetsDelete(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
